import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { CREATE_LOST_FOUND_ITEM, GET_LOST_FOUND_ITEMS } from '../graphql/lost-found-queries';
import LostFoundForm, { LOST_FOUND_CATEGORY } from './LostFoundForm';
import { useAuth } from '../contexts/AuthContext';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export default function CreateLostFoundModal({ isOpen, onClose, propertyId, propertyName, propertyData }) {
  const [error, setError] = useState('');
  const { currentUser } = useAuth();
  const formRef = useRef(null);
  
  const [createLostFoundItem, { loading }] = useMutation(CREATE_LOST_FOUND_ITEM, {
    refetchQueries: [{ query: GET_LOST_FOUND_ITEMS, variables: { page: 1, limit: 10, isArchived: false } }],
    awaitRefetchQueries: false,
    onCompleted: () => {
      onClose(true);
    },
    onError: (err) => {
      setError(err.message || 'An error occurred while creating the lost and found item. Please try again.');
    }
  });

  useEffect(() => {
    if (isOpen) {
      setError('');
    }
  }, [isOpen]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') onClose(false);
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleKeyDown]);

  const submitForm = () => {
    if (formRef.current?.submitForm) {
      formRef.current.submitForm();
      return;
    }
    setError('Unable to submit form. Please try again.');
  };

  const handleSubmit = async (formData) => {
    setError('');
    
    if (!formData.propertyId) {
      setError('Please select a valid property');
      return;
    }
    if (!UUID_REGEX.test(formData.propertyId)) {
      setError('Invalid property format. Please select a valid property.');
      return;
    }
    if (!formData.description?.trim()) {
      setError('Description is required');
      return;
    }
    
    const lostFoundData = {
      propertyId: formData.propertyId,
      reservationId: formData.reservationId,
      description: formData.description.trim(),
      category: formData.category || LOST_FOUND_CATEGORY.LOST_ITEM_FOUND,
      photos: formData.photos || [],
      returnOfficeInstructions: formData.returnOfficeInstructions,
    };
    
    try {
      await createLostFoundItem({
        variables: { input: lostFoundData }
      });
    } catch {
      setError('An unexpected error occurred. Please try again.');
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="lost-found-modal-title"
    >
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 transition-opacity"
          aria-hidden="true"
          onClick={() => onClose(false)}
        >
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full md:max-w-xl lg:max-w-2xl">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 id="lost-found-modal-title" className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Report Lost and Found Item
                </h3>
                
                {error && (
                  <div className="mb-4 bg-red-50 p-4 rounded-md">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">Error</h3>
                        <div className="mt-2 text-sm text-red-700">
                          <p>{error}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-2">
                  <LostFoundForm 
                    onSubmit={handleSubmit} 
                    isLoading={loading}
                    initialData={{
                      propertyId: propertyId || '',
                      propertySearchTerm: propertyName || '',
                      property: propertyData || (propertyId && propertyName ? {
                        id: propertyId,
                        name: propertyName
                      } : null),
                      ...(currentUser?.role === 'CALL_CENTER' && { category: LOST_FOUND_CATEGORY.REPORTED_LOST_ITEM })
                    }}
                    hideButtons
                    ref={formRef}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={submitForm}
              disabled={loading}
              className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm ${
                loading ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              {loading ? 'Submitting...' : 'Submit Item'}
            </button>
            <button
              type="button"
              onClick={() => onClose(false)}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
} 
